import { Fragment, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition } from '@headlessui/react';
import {
  ArrowLeftIcon,
  AcademicCapIcon,
  PlusIcon,
  XMarkIcon,
  ArchiveBoxIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { trainingApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import FeatureGate from '../components/FeatureGate';
import { StatusBadge, EmptyState, LoadingSpinner } from '../components/ui';
import type { BadgeVariant } from '../components/ui';

// ─── Constants ────────────────────────────────────────────────────────────────

const PROGRAM_STATUS_VARIANT: Record<string, BadgeVariant> = {
  ACTIVE: 'success',
  DRAFT: 'draft',
  ARCHIVED: 'neutral',
};

const COMPLETION_STATUS_VARIANT: Record<string, BadgeVariant> = {
  NOT_STARTED: 'neutral',
  IN_PROGRESS: 'info',
  COMPLETED: 'success',
  OVERDUE: 'danger',
  WAIVED: 'warning',
};

const COMPLETION_STATUS_LABEL: Record<string, string> = {
  NOT_STARTED: 'Not Started',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  OVERDUE: 'Overdue',
  WAIVED: 'Waived',
};

const CATEGORY_LABEL: Record<string, string> = {
  BSA_AML: 'BSA/AML',
  FAIR_LENDING: 'Fair Lending',
  MARKETING_COMPLIANCE: 'Marketing Compliance',
  HMDA: 'HMDA',
  PRIVACY: 'Privacy',
  CYBERSECURITY: 'Cybersecurity',
  VENDOR_MANAGEMENT: 'Vendor Management',
  GENERAL_COMPLIANCE: 'General Compliance',
  NEW_HIRE: 'New Hire',
  BOARD: 'Board',
  OTHER: 'Other',
};

const FREQUENCY_LABEL: Record<string, string> = {
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  SEMI_ANNUAL: 'Semi-Annual',
  ANNUAL: 'Annual',
  ONE_TIME: 'One-Time',
  CUSTOM: 'Custom',
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function TrainingDetailPage() {
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const { teamId } = useTeamId();

  const [showRecordModal, setShowRecordModal] = useState(false);

  // Fetch program with completions
  const { data, isLoading, error } = useQuery({
    queryKey: ['trainingProgram', teamId, id],
    queryFn: async () => {
      const res = await trainingApi.get(teamId!, id!);
      return res.data;
    },
    enabled: !!teamId && !!id,
  });

  const program = data;

  // Archive / Activate mutations
  const archiveMutation = useMutation({
    mutationFn: () => trainingApi.archive(teamId!, id!),
    onSuccess: () => {
      toast.success('Program archived');
      queryClient.invalidateQueries({ queryKey: ['trainingProgram', teamId, id] });
      queryClient.invalidateQueries({ queryKey: ['trainingPrograms'] });
      queryClient.invalidateQueries({ queryKey: ['trainingDashboard'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to archive'),
  });

  const activateMutation = useMutation({
    mutationFn: () => trainingApi.activate(teamId!, id!),
    onSuccess: () => {
      toast.success('Program activated');
      queryClient.invalidateQueries({ queryKey: ['trainingProgram', teamId, id] });
      queryClient.invalidateQueries({ queryKey: ['trainingPrograms'] });
      queryClient.invalidateQueries({ queryKey: ['trainingDashboard'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to activate'),
  });

  if (isLoading) return <LoadingSpinner size="lg" className="py-20" />;

  if (error || !program) {
    return (
      <div className="space-y-4">
        <Link
          to="/training"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Training
        </Link>
        <EmptyState
          icon={AcademicCapIcon}
          title="Program not found"
          description="This training program could not be loaded."
        />
      </div>
    );
  }

  const completions = program.completions || [];
  const completedCount = completions.filter((c: any) => c.status === 'COMPLETED').length;
  const overdueCount = completions.filter((c: any) => c.status === 'OVERDUE').length;
  const inProgressCount = completions.filter((c: any) => c.status === 'IN_PROGRESS').length;

  return (
    <FeatureGate feature="training_management">
      <div className="space-y-6">
        {/* Back link + header */}
        <div>
          <Link
            to="/training"
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to Training
          </Link>

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-blue-50 flex-shrink-0 mt-0.5">
                <AcademicCapIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {program.title}
                </h1>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <StatusBadge variant={PROGRAM_STATUS_VARIANT[program.status] || 'neutral'}>
                    {program.status}
                  </StatusBadge>
                  <span className="text-sm text-gray-500">
                    {CATEGORY_LABEL[program.category] || program.category}
                  </span>
                  <span className="text-gray-300">|</span>
                  <span className="text-sm text-gray-500">
                    {FREQUENCY_LABEL[program.frequency] || program.frequency}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {program.status === 'ACTIVE' && (
                <button
                  onClick={() => archiveMutation.mutate()}
                  disabled={archiveMutation.isPending}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <ArchiveBoxIcon className="h-4 w-4" />
                  Archive
                </button>
              )}
              {(program.status === 'DRAFT' || program.status === 'ARCHIVED') && (
                <button
                  onClick={() => activateMutation.mutate()}
                  disabled={activateMutation.isPending}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-700"
                >
                  <CheckCircleIcon className="h-4 w-4" />
                  Activate
                </button>
              )}
              <button
                onClick={() => setShowRecordModal(true)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-700"
              >
                <PlusIcon className="h-4 w-4" />
                Record Completion
              </button>
            </div>
          </div>
        </div>

        {/* Program details card */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900">
              Program Details
            </h2>
          </div>
          <div className="px-6 py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {program.description && (
              <div className="sm:col-span-2 lg:col-span-3">
                <dt className="text-xs font-medium text-gray-500 uppercase">Description</dt>
                <dd className="mt-1 text-sm text-gray-900 whitespace-pre-line">{program.description}</dd>
              </div>
            )}
            {program.provider && (
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase">Provider</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {program.provider}
                  {program.providerUrl && (
                    <a
                      href={program.providerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-1.5 inline-flex items-center text-blue-600 hover:text-blue-800"
                    >
                      <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                    </a>
                  )}
                </dd>
              </div>
            )}
            {program.durationMinutes && (
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase">Duration</dt>
                <dd className="mt-1 text-sm text-gray-900">{program.durationMinutes} minutes</dd>
              </div>
            )}
            {program.regulatoryBasis && (
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase">Regulatory Basis</dt>
                <dd className="mt-1 text-sm text-gray-900">{program.regulatoryBasis}</dd>
              </div>
            )}
            {program.gracePeriodDays != null && (
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase">Grace Period</dt>
                <dd className="mt-1 text-sm text-gray-900">{program.gracePeriodDays} days</dd>
              </div>
            )}
            {program.effectiveDate && (
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase">Effective Date</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(program.effectiveDate).toLocaleDateString()}
                </dd>
              </div>
            )}
            {program.expirationDate && (
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase">Expiration Date</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(program.expirationDate).toLocaleDateString()}
                </dd>
              </div>
            )}
          </div>
        </div>

        {/* Completion stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-500">Total Tracked</div>
            <div className="mt-1 text-2xl font-bold text-gray-900">{completions.length}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-green-600">Completed</div>
            <div className="mt-1 text-2xl font-bold text-gray-900">{completedCount}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-blue-600">In Progress</div>
            <div className="mt-1 text-2xl font-bold text-gray-900">{inProgressCount}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-red-600">Overdue</div>
            <div className="mt-1 text-2xl font-bold text-gray-900">{overdueCount}</div>
          </div>
        </div>

        {/* Completions table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900">
              Completion Records
            </h2>
          </div>
          {completions.length === 0 ? (
            <EmptyState
              icon={ClockIcon}
              title="No completion records"
              description="Record a completion to start tracking who has completed this training."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Person</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completed</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {completions.map((c: any) => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {c.userName || c.userId}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                        {c.userRole || '—'}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        <StatusBadge variant={COMPLETION_STATUS_VARIANT[c.status] || 'neutral'}>
                          {COMPLETION_STATUS_LABEL[c.status] || c.status}
                        </StatusBadge>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                        {new Date(c.dueDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                        {c.completedAt ? new Date(c.completedAt).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                        {c.score != null ? `${c.score}%` : '—'}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-600 max-w-xs truncate">
                        {c.notes || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Record Completion Dialog */}
        <RecordCompletionDialog
          open={showRecordModal}
          onClose={() => setShowRecordModal(false)}
          teamId={teamId!}
          programId={id!}
          queryClient={queryClient}
        />
      </div>
    </FeatureGate>
  );
}

// ─── Record Completion Dialog ─────────────────────────────────────────────────

function RecordCompletionDialog({
  open,
  onClose,
  teamId,
  programId,
  queryClient,
}: {
  open: boolean;
  onClose: () => void;
  teamId: string;
  programId: string;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const [form, setForm] = useState({
    userId: '',
    userName: '',
    userRole: '',
    status: 'COMPLETED',
    dueDate: new Date().toISOString().split('T')[0],
    completedAt: new Date().toISOString().split('T')[0],
    score: '',
    notes: '',
  });

  const mutation = useMutation({
    mutationFn: (data: any) => trainingApi.recordCompletion(teamId, programId, data),
    onSuccess: () => {
      toast.success('Completion recorded');
      queryClient.invalidateQueries({ queryKey: ['trainingProgram', teamId, programId] });
      queryClient.invalidateQueries({ queryKey: ['trainingPrograms'] });
      queryClient.invalidateQueries({ queryKey: ['trainingDashboard'] });
      onClose();
      setForm({
        userId: '',
        userName: '',
        userRole: '',
        status: 'COMPLETED',
        dueDate: new Date().toISOString().split('T')[0],
        completedAt: new Date().toISOString().split('T')[0],
        score: '',
        notes: '',
      });
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to record completion'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      ...form,
      score: form.score ? parseFloat(form.score) : undefined,
      completedAt: form.status === 'COMPLETED' ? form.completedAt : undefined,
    });
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    Record Completion
                  </Dialog.Title>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        User ID *
                      </label>
                      <input
                        type="text"
                        required
                        value={form.userId}
                        onChange={(e) => setForm({ ...form, userId: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                        placeholder="User ID"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        value={form.userName}
                        onChange={(e) => setForm({ ...form, userName: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                        placeholder="Full name"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Role
                      </label>
                      <input
                        type="text"
                        value={form.userRole}
                        onChange={(e) => setForm({ ...form, userRole: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                        placeholder="e.g., Loan Officer"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select
                        value={form.status}
                        onChange={(e) => setForm({ ...form, status: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      >
                        <option value="COMPLETED">Completed</option>
                        <option value="IN_PROGRESS">In Progress</option>
                        <option value="NOT_STARTED">Not Started</option>
                        <option value="WAIVED">Waived</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Due Date *
                      </label>
                      <input
                        type="date"
                        required
                        value={form.dueDate}
                        onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      />
                    </div>
                    {form.status === 'COMPLETED' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Completed At
                        </label>
                        <input
                          type="date"
                          value={form.completedAt}
                          onChange={(e) => setForm({ ...form, completedAt: e.target.value })}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Score (0-100)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={form.score}
                      onChange={(e) => setForm({ ...form, score: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      placeholder="Optional"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes
                    </label>
                    <textarea
                      rows={2}
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={mutation.isPending}
                      className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
                    >
                      {mutation.isPending ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
